import { Pencil } from "lucide-react";
import { useState } from "react";

import type { TakeoffConfiguration } from "@/api/client";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";

const FIELD =
  "h-8 w-full rounded-md border border-line-strong bg-surface px-2.5 text-[13px] text-ink placeholder:text-muted focus:border-accent focus:outline-none";

interface Props {
  configurations: readonly TakeoffConfiguration[];
  /** Unit ids with a panel on the sheet being looked at. */
  onSheet: ReadonlySet<string>;
  activeId: string | null;
  onActivate: (unitId: string | null) => void;
  /** Resolves true when the name was saved. */
  onName: (unitId: string, mark: string) => Promise<boolean>;
  /** Opens the rename dialog for a named configuration. */
  onRename: (unitId: string) => void;
  onMerge: (unitId: string, intoUnitId: string) => Promise<boolean>;
  error?: string | null;
  /** A role that may not review sees the configurations but cannot name or merge them. */
  readOnly?: boolean;
}

function NameField({
  unitId,
  label,
  onName,
}: {
  unitId: string;
  label: string;
  onName: Props["onName"];
}) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <form
      className="flex gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        const mark = value.trim();
        if (!mark || saving) {
          return;
        }
        setSaving(true);
        void onName(unitId, mark).then((ok) => {
          setSaving(false);
          if (ok) {
            setValue("");
          }
        });
      }}
    >
      <input
        type="text"
        value={value}
        aria-label={label}
        className={FIELD}
        placeholder="Unit code"
        onChange={(event) => setValue(event.target.value)}
      />
      <Button type="submit" size="sm" variant="primary" disabled={!value.trim() || saving}>
        {saving ? "Saving…" : "Name"}
      </Button>
    </form>
  );
}

/**
 * The key to the markup: one row per configuration in the colour it is drawn in. A
 * configuration the estimator has not named yet carries a field to name it, and the name
 * then applies to every panel of it, on screen and in the download.
 */
export function Legend({
  configurations,
  onSheet,
  activeId,
  onActivate,
  onName,
  onRename,
  onMerge,
  error = null,
  readOnly = false,
}: Props) {
  const [scope, setScope] = useState<"sheet" | "all">("sheet");
  const shown =
    scope === "sheet" ? configurations.filter((c) => onSheet.has(c.unit_id)) : configurations;

  return (
    <section aria-label="Configurations" className="flex flex-col">
      <header className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
        <h2 className="text-xs font-semibold tracking-wide text-muted uppercase">Configurations</h2>
        <div
          className="flex rounded-md border border-line bg-sunken p-0.5 text-xs"
          role="group"
          aria-label="Which configurations to list"
        >
          {(["sheet", "all"] as const).map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={scope === key}
              onClick={() => setScope(key)}
              className={cn(
                "rounded px-2 py-0.5 font-medium text-ink-2",
                scope === key && "bg-surface text-ink shadow-card",
              )}
            >
              {key === "sheet" ? "This sheet" : "All"}
            </button>
          ))}
        </div>
      </header>
      {error ? (
        <p className="mx-4 mb-2 text-[13px] text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="flex flex-col gap-0.5 px-2 pb-4">
        {shown.map((c) => {
          const active = c.unit_id === activeId;
          return (
            <li
              key={c.unit_id}
              data-unit={c.unit_id}
              className={cn("rounded-lg", active && "bg-sunken ring-1 ring-line-strong")}
            >
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onActivate(active ? null : c.unit_id)}
                className="flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-sunken"
              >
                <span
                  aria-hidden="true"
                  className="mt-0.5 size-3.5 shrink-0 rounded-sm ring-1 ring-black/10"
                  style={{ background: c.colour }}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block truncate font-mono text-[13px] font-medium",
                      c.named ? "text-ink" : "text-muted italic",
                    )}
                  >
                    {c.mark}
                  </span>
                  {c.summary ? (
                    <span className="block truncate text-xs text-muted" title={c.summary}>
                      {c.summary}
                    </span>
                  ) : null}
                </span>
                <span className="tabular shrink-0 text-[13px] font-medium text-ink-2">
                  {c.count.toLocaleString("en-US")}
                </span>
              </button>
              {!c.named && !readOnly ? (
                <div className="px-2 pb-2 pl-8">
                  <NameField unitId={c.unit_id} label={`Name for ${c.mark}`} onName={onName} />
                </div>
              ) : null}
              {active ? (
                <div className="flex flex-col gap-3 px-2 pt-1 pb-3 pl-8">
                  {c.sizes.length ? (
                    <ul className="flex flex-col gap-1 text-xs">
                      {c.sizes.slice(0, 6).map((size) => (
                        <li
                          key={`${size.width}x${size.height}`}
                          className="tabular flex justify-between text-ink-2"
                        >
                          <span>
                            {size.width} × {size.height}
                          </span>
                          <span className="text-muted">{size.count}</span>
                        </li>
                      ))}
                      {c.sizes.length > 6 ? (
                        <li className="text-muted">and {c.sizes.length - 6} more sizes</li>
                      ) : null}
                    </ul>
                  ) : null}
                  {c.named && !readOnly ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="self-start"
                      onClick={() => onRename(c.unit_id)}
                    >
                      <Pencil aria-hidden="true" />
                      Rename
                    </Button>
                  ) : null}
                  {readOnly ? null : (
                    <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
                      <span>Merge into</span>
                      <select
                        value=""
                        aria-label={`Merge ${c.mark} into`}
                        className={FIELD}
                        onChange={(event) => {
                          const into = event.target.value;
                          if (into) {
                            void onMerge(c.unit_id, into);
                          }
                        }}
                      >
                        <option value="">Choose a configuration</option>
                        {configurations
                          .filter((other) => other.unit_id !== c.unit_id)
                          .map((other) => (
                            <option key={other.unit_id} value={other.unit_id}>
                              {other.mark} ({other.count})
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
        {shown.length === 0 ? (
          <li className="px-2 py-3 text-[13px] text-muted">
            {scope === "sheet" ? "No panels are marked on this sheet." : "No configurations yet."}
          </li>
        ) : null}
      </ul>
    </section>
  );
}
